import { useCallback } from 'react';

import { FileContext } from '@/types/schema';
import * as diagnostics from '@/utils/diagnostics';

import { useUploadCsvImportFileV2Mutation } from '../../graphql/FileUploaderV2.generated';
import {
  FileUploadError,
  FileUploadErrorCode,
  getSha256HashFromFile,
} from '../FileUploader.utils';
import { useFakeUploadProgress } from './useFakeUploadProgress';
import { UploadedFileData } from './useUploadFile';

export interface UseUploadCSVV2Props<_Mutation> {
  // When uploading a single file, we can set incremental progress values.
  setSingleFileUploadProgress: React.Dispatch<React.SetStateAction<number>>;
  // When uploading multiple files, we can increment the progress value.
  setMultipleFileUploadProgress: React.Dispatch<React.SetStateAction<number>>;
  // File context for categorizing uploads
  context?: FileContext;
  // Household ID for HOUSEHOLD context files
  householdId?: string | null;
}

export function useUploadCSVV2<Mutation>({
  setSingleFileUploadProgress,
  setMultipleFileUploadProgress,
  context = FileContext.Household,
  householdId,
}: UseUploadCSVV2Props<Mutation>) {
  const [uploadCSVImportFileV2Mutation] = useUploadCsvImportFileV2Mutation();
  const { start: startFakeProgress, stop: stopFakeProgress } =
    useFakeUploadProgress(setSingleFileUploadProgress);

  const logUploadError = useCallback(
    (error: Error, file: File) => {
      diagnostics.error('CSV file upload failed', error, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        householdId,
        context,
      });
    },
    [householdId, context]
  );

  const uploadFile = useCallback(
    async (file: File): Promise<UploadedFileData<Mutation>> => {
      if (file.size === 0) {
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(FileUploadErrorCode.EmptyFile);
        throw error;
      }

      startFakeProgress();

      let hash: string;
      try {
        hash = await getSha256HashFromFile(file);
      } catch (err) {
        stopFakeProgress();
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(
          FileUploadErrorCode.HashFailed,
          err as Error
        );
        logUploadError(error, file);
        throw error;
      }

      try {
        const result = await uploadCSVImportFileV2Mutation({
          variables: {
            input: {
              contentsSha256: hash,
              context,
              householdID:
                context === FileContext.Household ? householdId : undefined,
            },
            upload: file,
          },
          context: {
            hasUpload: true,
          },
        });

        stopFakeProgress();
        setSingleFileUploadProgress(100);
        setMultipleFileUploadProgress((lastValue) => lastValue + 1);

        return { file, result } as UploadedFileData<Mutation>;
      } catch (err) {
        stopFakeProgress();
        setSingleFileUploadProgress(0);
        const error = new FileUploadError(
          FileUploadErrorCode.UploadFailed,
          err as Error
        );
        logUploadError(error, file);
        throw error;
      }
    },
    [
      uploadCSVImportFileV2Mutation,
      startFakeProgress,
      stopFakeProgress,
      setSingleFileUploadProgress,
      setMultipleFileUploadProgress,
      context,
      householdId,
      logUploadError,
    ]
  );

  return {
    uploadFile,
  };
}
